import { cur } from '@/lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

/**
 * "Upcoming Expenses" + "Document Expiry Alerts" section. Both list-based,
 * both show overdue/days-left badge. Extracted from DashboardPage to keep
 * that file under 700 LOC.
 */
export const DashboardAlertsCards = ({ upcomingExpenses = [], documentAlerts = [] }) => {
  return (
    <>
      {upcomingExpenses.length > 0 && (
        <Card className="border-border border-error/30 bg-error/5">
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2">
              <AlertTriangle size={18} className="text-error" />
              Upcoming Expenses ({upcomingExpenses.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {upcomingExpenses.map((e, i) => (
                <div key={i} className="flex justify-between items-center p-3 bg-background rounded-lg border" data-testid="upcoming-expense">
                  <div>
                    <div className="font-medium text-sm">{e.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {e.category} | Due: {format(new Date(e.due_date), 'MMM dd, yyyy')}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold"> {cur()} {Number(e.amount || 0).toFixed(2)}</div>
                    <Badge className={e.days_left < 0 ? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'}>
                      {e.days_left < 0 ? `${Math.abs(e.days_left)}d overdue` : `${e.days_left}d left`}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {documentAlerts.length > 0 && (
        <Card className="border-border border-warning/50 bg-warning/5">
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2">
              <AlertTriangle size={18} className="text-warning" />
              Document Expiry Alerts ({documentAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {documentAlerts.slice(0, 5).map((a) => (
                <div
                  key={a.id + a.type}
                  className={`flex justify-between items-center p-3 rounded-lg ${a.days_left < 0 ? 'bg-error/10' : 'bg-warning/10'}`}
                  data-testid="dashboard-alert"
                >
                  <div>
                    <div className="font-medium text-sm">{a.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {a.related_to} | {format(new Date(a.expiry_date), 'MMM dd, yyyy')}
                    </div>
                  </div>
                  <Badge className={a.days_left < 0 ? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'}>
                    {a.days_left < 0 ? `${Math.abs(a.days_left)}d overdue` : `${a.days_left}d left`}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default DashboardAlertsCards;
